import { navigationStickActions, settingMenu } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.redux';
import { selectNavigationStick } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.selectors';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';
import { selectIsAutoTaskDetailsModified } from 'store/redux-store/placement-requests/selectors';
import {
  placementRequestsAction,
  placementRequestsReducer,
  placementRequestsSliceKey,
} from 'store/redux-store/placement-requests/slice';
import { ConfirmationDialog } from './common/ConfirmationDialog';
import { AutoTaskManagementWrapper } from './componenets/AutoTaskManagementWrapper';
import { placementRequestsSaga } from 'store/redux-store/placement-requests/saga';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { useConfirmExit } from 'utils/customHooks/useConfirmExit';

export const AutoTaskManagementPage = () => {
  useInjectSaga({ key: placementRequestsSliceKey, saga: placementRequestsSaga });
  useInjectReducer({ key: placementRequestsSliceKey, reducer: placementRequestsReducer });
  const dispatch = useDispatch();
  const history = useNavigate();
  const { t } = useTranslation();
  const { selectedMenu } = useSelector(selectNavigationStick);
  const isDirty = useSelector(selectIsAutoTaskDetailsModified);
  const location = useLocation();

  const [triggerExit, setTriggerExit] = useState({
    isExit: false,
    path: '',
  });
  const [isVisibleDialog, setVisibleDialog] = useState(false);
  const handleGoToIntendedPage = useCallback(location => history(location), [history]);

  const { blockExit, allowExit } = useConfirmExit();

  // Handle React Router navigation
  const shouldBlock = useCallback(
    ({ nextLocation }) => {
      if (nextLocation.pathname !== location.pathname && isDirty) {
        return true;
      }
      return false;
    },
    [location.pathname, isDirty],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (isDirty && blocker.state === 'blocked') {
      setVisibleDialog(true);
    }
  }, [blocker, isDirty]);

  useEffect(() => {
    if (triggerExit.isExit && triggerExit.path) {
      dispatch(placementRequestsAction.reset());
      handleGoToIntendedPage(triggerExit.path);
    }
  }, [handleGoToIntendedPage, triggerExit.isExit, triggerExit.path, dispatch]);

  // Handle browser navigation (back/forward/refresh)
  useEffect(() => {
    if (isDirty) {
      blockExit(() => {
        if (location.pathname !== '/settings/auto-task-management') {
          setTriggerExit(obj => ({ ...obj, path: location.pathname }));
          setVisibleDialog(true);
        }
      });
    } else {
      allowExit();
    }
  }, [isDirty, location.pathname, blockExit, allowExit]);

  /** Logic to highlight sidenav if routes are directly accessed */
  React.useEffect(() => {
    if (!['settings', 'templateEditor'].includes(selectedMenu?.key)) {
      const menu = settingMenu.subMenu.find(x => x.key === 'autoTaskManagement');
      dispatch(navigationStickActions.setSelectedMenu(settingMenu));
      dispatch(navigationStickActions.setSelectedSubMenu(menu));
    }
  }, [selectedMenu?.key, dispatch]);

  const keepChanges = () => {
    setVisibleDialog(false);

    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    const menu = settingMenu.subMenu.find(x => x.key === 'autoTaskManagement');
    dispatch(navigationStickActions.setSelectedMenu(settingMenu));
    dispatch(navigationStickActions.setSelectedSubMenu(menu));
  };

  const handleDiscard = () => {
    setTriggerExit(obj => ({
      ...obj,
      isExit: true,
    }));
    setVisibleDialog(false);

    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handleSave = () => {
    dispatch(placementRequestsAction.updateAutoTaskManagement());
    setTriggerExit(obj => ({
      ...obj,
      isExit: true,
    }));
    setVisibleDialog(false);

    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const saveDialogActions = [
    {
      text: t(`autoTaskManagement.button.cancel`),
      onClick: keepChanges,
    },
    {
      text: t(`autoTaskManagement.button.discard`),
      onClick: handleDiscard,
      color: 'tertiary',
      variant: 'contained',
    },
    {
      text: t(`autoTaskManagement.button.save`),
      variant: 'contained',
      color: 'primary',
      onClick: handleSave,
    },
  ];

  return (
    <>
      <AutoTaskManagementWrapper />
      <ConfirmationDialog
        dialogeOpen={isVisibleDialog}
        keepChanges={keepChanges}
        handleDiscard={handleDiscard}
        dialogActions={saveDialogActions}
        dialogTitle={t(`autoTaskManagement.dialogModal.saveChanges`)}
        dialogeText={t(`autoTaskManagement.dialogModal.saveAndExit`)}
      />
    </>
  );
};
